import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { formatCLP as fmt, hoyISO, haceDiasISO } from '../lib/format';
import { calcularFinanzas, ahorradoPorMeta, totalAbonadoPorCompromiso, equivalenteMensual, gastoFijoPagado } from '../lib/finanzas';
import { stockPorProducto, gananciaVenta, totalVenta } from '../lib/negocio';

// Carga datos de Supabase y los convierte en texto para Billuyo
async function buildFinancialContext(userId) {
  if (!userId) return 'No hay datos financieros registrados todavía.';
  try {
    const inicioMes = hoyISO().slice(0, 8) + '01';
    const res = await Promise.all([
      supabase.from('bolsillos').select('*').eq('user_id', userId),
      supabase.from('bolsillo_movimientos').select('bolsillo_id, tipo, monto').eq('user_id', userId),
      supabase.from('compromisos').select('*').eq('user_id', userId),
      supabase.from('abonos').select('compromiso_id, monto').eq('user_id', userId),
      supabase.from('metas').select('*').eq('user_id', userId),
      supabase.from('abonos_meta').select('meta_id, bolsillo_id, monto').eq('user_id', userId),
      supabase.from('deudas').select('*').eq('user_id', userId).eq('activa', true),
      supabase.from('pagos_deuda').select('deuda_id, monto').eq('user_id', userId),
      supabase.from('gastos_fijos').select('*').eq('user_id', userId),
      supabase.from('prestamos').select('*').eq('user_id', userId).eq('cobrado', false),
      supabase.from('cobros_prestamo').select('prestamo_id, monto').eq('user_id', userId),
      supabase.from('compras').select('total').eq('user_id', userId).gte('fecha', haceDiasISO(30)),
      supabase.from('productos').select('*').eq('user_id', userId).eq('activo', true),
      supabase.from('movimientos_stock').select('producto_id, cantidad').eq('user_id', userId),
      supabase.from('ventas').select('*').eq('user_id', userId),
    ]);
    const [bolsillos, movimientos, compromisos, abonos, metas, abonosMeta, deudas, pagosDeuda,
      gastosFijos, prestamos, cobros, compras30, productos, movStock, ventas] = res.map(r => r.data || []);

    let ctx = '';

    if (bolsillos.length === 0) {
      ctx += 'Aún no hay categorías de dinero creadas.\n';
    } else {
      const fin = calcularFinanzas({ bolsillos, movimientos, compromisos, abonos, abonosMeta });
      const abonado = totalAbonadoPorCompromiso(abonos);
      bolsillos.forEach(b => {
        const { saldo, comprometido, enMetas, disponible } = fin.porBolsillo(b.id);
        ctx += `Categoría "${b.nombre}" (${b.tipo}):\n`;
        ctx += `  Saldo: ${fmt(saldo)} | Comprometido: ${fmt(comprometido)} | Reservado en metas: ${fmt(enMetas)} | Disponible real: ${fmt(disponible)}\n`;
        compromisos.filter(c => c.bolsillo_id === b.id && c.estado === 'pendiente').forEach(c => {
          const pendiente = Math.max(0, c.monto_total - (abonado[c.id] || 0));
          ctx += `    • Compromiso ${c.nombre}: ${fmt(pendiente)} pendiente de ${fmt(c.monto_total)}\n`;
        });
      });
      ctx += `\nRESUMEN TOTAL:\n`;
      ctx += `  Saldo total: ${fmt(fin.totalSaldo)}\n`;
      ctx += `  Comprometido: ${fmt(fin.totalComprometido)}\n`;
      ctx += `  Reservado en metas: ${fmt(fin.totalEnMetas)}\n`;
      ctx += `  Disponible real para gastar: ${fmt(fin.totalDisponible)}\n`;
    }

    if (metas.length > 0) {
      const ahorrado = ahorradoPorMeta(abonosMeta);
      ctx += `\nMETAS DE AHORRO:\n`;
      metas.forEach(m => {
        const actual = ahorrado[m.id] || 0;
        const pct = m.monto_objetivo > 0 ? Math.round((actual / m.monto_objetivo) * 100) : 0;
        ctx += `  • ${m.nombre}: ${fmt(actual)} de ${fmt(m.monto_objetivo)} (${pct}%)`;
        if (m.fecha_objetivo) ctx += ` — límite ${m.fecha_objetivo}`;
        ctx += '\n';
      });
    }

    if (gastosFijos.length > 0) {
      const pendientes = gastosFijos.filter(g => !gastoFijoPagado(g));
      const totalMensual = gastosFijos.reduce((s, g) => s + equivalenteMensual(g), 0);
      ctx += `\nGASTOS FIJOS (total mensual equivalente ${fmt(totalMensual)}):\n`;
      pendientes.forEach(g => { ctx += `  • Pendiente: ${g.nombre} ${fmt(g.monto)} (${g.frecuencia})\n`; });
      if (pendientes.length === 0) ctx += '  Todos pagados este período.\n';
    }

    if (deudas.length > 0) {
      ctx += `\nDEUDAS ACTIVAS:\n`;
      deudas.forEach(d => {
        const pagado = pagosDeuda.filter(p => p.deuda_id === d.id).reduce((s, p) => s + Number(p.monto), 0);
        ctx += `  • ${d.nombre}${d.acreedor ? ` (${d.acreedor})` : ''}: ${fmt(Math.max(0, d.monto_original - pagado))} pendiente de ${fmt(d.monto_original)}`;
        if (d.cuota_mensual) ctx += ` — cuota ${fmt(d.cuota_mensual)}`;
        if (d.dia_vencimiento) ctx += ` (vence día ${d.dia_vencimiento})`;
        ctx += '\n';
      });
    }

    if (prestamos.length > 0) {
      ctx += `\nME DEBEN (préstamos por cobrar):\n`;
      prestamos.forEach(p => {
        const cobrado = cobros.filter(c => c.prestamo_id === p.id).reduce((s, c) => s + Number(c.monto), 0);
        ctx += `  • ${p.persona}: ${fmt(Math.max(0, p.monto - cobrado))}${p.fecha_esperada ? ` — paga el ${p.fecha_esperada}` : ''}\n`;
      });
    }

    if (compras30.length > 0) {
      ctx += `\nCOMPRAS últimos 30 días: ${compras30.length} compras por ${fmt(compras30.reduce((s, c) => s + Number(c.total || 0), 0))}\n`;
    }

    if (productos.length > 0) {
      const stock = stockPorProducto(movStock, ventas);
      const ventasMes = ventas.filter(v => v.fecha >= inicioMes && !v.devuelta);
      const ventasConMonto = ventasMes.filter(v => Number(v.precio_unitario) > 0);
      const invertido = productos.reduce((s, p) => s + Math.max(0, stock[p.id] || 0) * p.costo_unitario, 0);
      const bajos = productos.filter(p => (stock[p.id] || 0) <= p.stock_minimo);
      ctx += `\nMI NEGOCIO (venta de productos):\n`;
      ctx += `  Este mes: ${ventasMes.length} ventas, vendido ${fmt(ventasConMonto.reduce((s, v) => s + totalVenta(v), 0))}, ganancia real ${fmt(ventasConMonto.reduce((s, v) => s + gananciaVenta(v), 0))}\n`;
      ctx += `  Dinero invertido en stock (a costo): ${fmt(invertido)}\n`;
      if (bajos.length) ctx += `  Productos con stock bajo: ${bajos.map(p => `${p.sku} ${p.nombre} (quedan ${stock[p.id] || 0})`).join(', ')}\n`;
    }

    return ctx;
  } catch (e) {
    return 'Error leyendo datos financieros.';
  }
}

const SUGERENCIAS = [
  '¿Cómo está mi situación financiera?',
  '¿Puedo darme un gusto esta semana?',
];

function FinanceAI({ userId, userName }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: `¡Hola${userName ? ` ${userName}` : ''}! Soy Billuyo, tu asistente financiero 👋\n\nVeo tus categorías, compromisos, metas, deudas y tu negocio en tiempo real. Puedo decirte cuánto dinero tienes disponible de verdad, si puedes darte un gusto, y ayudarte a entender tu situación financiera.\n\n¿En qué te ayudo?`,
      }]);
    }
  }, [open, messages.length, userName]);

  useEffect(() => {
    if (open) {
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [messages, open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 200);
  }, [open]);

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;

    setInput('');
    setError('');

    const newMessages = [...messages, { role: 'user', content: msg }];
    setMessages(newMessages);
    setLoading(true);

    // Solo enviar el historial real (sin el mensaje de bienvenida inicial)
    const historial = newMessages
      .filter((m, i) => !(i === 0 && m.role === 'assistant'))
      .map(m => ({ role: m.role, content: m.content }));

    try {
      const [financialContext, { data: { session } }] = await Promise.all([
        buildFinancialContext(userId),
        supabase.auth.getSession(),
      ]);

      const res = await fetch('/api/claude-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // La función del servidor valida la sesión para que nadie más use la API key
          Authorization: `Bearer ${session?.access_token || ''}`,
        },
        body: JSON.stringify({
          messages: historial,
          financialContext,
          userName: userName || 'Usuario',
        }),
      });

      if (!(res.headers.get('content-type') || '').includes('application/json')) {
        throw new Error('Billuyo no está disponible aquí. En local, la app debe correr con "npx wrangler pages dev" para que funcione la función del servidor.');
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`);

      setMessages(prev => [...prev, { role: 'assistant', content: data.content }]);
    } catch (e) {
      setError(e.message.includes('Failed to fetch')
        ? 'No se pudo conectar con Billuyo. Revisa tu conexión a internet.'
        : e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setError('');
  };

  return (
    <>
      {/* Botón flotante */}
      <button
        className={`ai-fab ${open ? 'ai-fab-open' : ''}`}
        onClick={() => setOpen(o => !o)}
        title="Asistente financiero IA"
        aria-label="Abrir asistente IA"
      >
        {open ? (
          <span className="ai-fab-icon">✕</span>
        ) : (
          <>
            <span className="ai-fab-icon">✦</span>
            <span className="ai-fab-label">Billuyo</span>
          </>
        )}
      </button>

      {/* Panel de chat */}
      {open && (
        <div className="ai-panel">
          {/* Header */}
          <div className="ai-panel-header">
            <div className="ai-header-left">
              <div className="ai-avatar">✦</div>
              <div>
                <span className="ai-name">Billuyo</span>
                <span className="ai-status">Tu asistente financiero</span>
              </div>
            </div>
            <div className="ai-header-actions">
              <button className="ai-clear-btn" onClick={clearChat} title="Limpiar chat">↺</button>
              <button className="ai-close-btn" onClick={() => setOpen(false)}>✕</button>
            </div>
          </div>

          {/* Mensajes */}
          <div className="ai-messages">
            {messages.map((m, i) => (
              <div key={i} className={`ai-message ${m.role}`}>
                {m.role === 'assistant' && <div className="ai-msg-avatar">✦</div>}
                <div className="ai-msg-bubble">
                  {m.content.split('\n').map((line, j, lines) => (
                    <React.Fragment key={j}>
                      {line}
                      {j < lines.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}

            {loading && (
              <div className="ai-message assistant">
                <div className="ai-msg-avatar">✦</div>
                <div className="ai-msg-bubble ai-typing">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}

            {error && (
              <div className="ai-error-msg">
                ⚠ {error}
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Sugerencias rápidas (solo si no hay historial de usuario) */}
          {messages.filter(m => m.role === 'user').length === 0 && (
            <div className="ai-suggestions">
              {SUGERENCIAS.map((s, i) => (
                <button key={i} className="ai-suggestion-btn" onClick={() => sendMessage(s)}>
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="ai-input-row">
            <textarea
              ref={inputRef}
              className="ai-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Pregúntame algo sobre tus finanzas..."
              rows={1}
              disabled={loading}
            />
            <button
              className="ai-send-btn"
              onClick={() => sendMessage()}
              disabled={loading || !input.trim()}
              title="Enviar"
            >
              ➤
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default FinanceAI;
